import { useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../api";
import {
  CODIGO_FORMULARIO,
  NIVEIS_DESEMPENHO,
  modeloDoColaborador,
  modelosDaEmpresa,
} from "../avaliacao-modelos";
import {
  EMPRESAS,
  EMPRESA_PADRAO,
  daEmpresa,
  empresaDoColaborador,
  nomeDaEmpresa,
  type EmpresaKey,
} from "../empresas";
import type { Row } from "../types";
import { ordenarPorNome } from "../ordem";
import FormularioAvaliacao, {
  EscalaNiveis,
  RESPOSTAS_VAZIAS,
  mediaDasNotas,
  nivelDe,
  type RespostasFormulario,
} from "./FormularioAvaliacao";
import { IconClose, IconStar, IconDownload, IconLink, IconCopy, IconCheck } from "../icons";

/**
 * O RH aplicando o formulário FO 094 direto na tela — o caso de quem avalia na
 * conversa de desempenho, com o colaborador do lado (o link do gestor continua
 * existindo para quem prefere responder de casa).
 *
 * Primeiro escolhe-se a EMPRESA (SERCAMP ou Move Me): ela decide os dois
 * campos seguintes — quem pode ser avaliado e quais formulários existem. As
 * duas empresas têm cargos de mesmo nome ("CONSULTOR COMERCIAL") com perguntas
 * diferentes, então misturar as listas colocaria a consultora da loja
 * respondendo o formulário do consultor da SERCAMP.
 *
 * Escolhido o colaborador, o formulário do CARGO dele já vem marcado; dá para
 * trocar. No fim, "Imprimir" gera a folha para assinatura, igual à planilha.
 */
export default function AplicarAvaliacaoModal({
  colaboradores,
  colaboradorInicial,
  onClose,
  onSalvo,
}: {
  colaboradores: Row[];
  colaboradorInicial?: number | null;
  onClose: () => void;
  onSalvo: (mensagem: string) => void | Promise<void>;
}) {
  /**
   * A empresa começa na do colaborador que veio clicado na lista ("Avaliar" na
   * linha da pessoa). Sem ninguém escolhido, começa na SERCAMP.
   */
  const [empresa, setEmpresa] = useState<EmpresaKey>(() => {
    const inicial = colaboradorInicial
      ? colaboradores.find((c) => Number(c.id) === Number(colaboradorInicial))
      : null;
    return inicial ? empresaDoColaborador(inicial) : EMPRESA_PADRAO;
  });

  // Os formulários vêm do próprio catálogo (src/avaliacao-modelos.ts), não de
  // uma chamada: a lista NUNCA pode aparecer vazia por causa de rede.
  const catalogo = {
    codigo_formulario: CODIGO_FORMULARIO,
    niveis: [...NIVEIS_DESEMPENHO],
    modelos: modelosDaEmpresa(empresa),
  };
  /** Só quem é da empresa escolhida entra na lista de avaliáveis. */
  const listaColaboradores = useMemo(
    () => ordenarPorNome(daEmpresa(colaboradores, empresa), (c) => c.nome),
    [colaboradores, empresa],
  );
  const [colaboradorId, setColaboradorId] = useState<string>(
    colaboradorInicial ? String(colaboradorInicial) : "",
  );
  /** Link individual gerado para o responsável responder. */
  const [link, setLink] = useState<{ url: string; gestor: string } | null>(null);
  const [gerandoLink, setGerandoLink] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [modeloKey, setModeloKey] = useState("");
  const [periodo, setPeriodo] = useState(String(new Date().getFullYear()));
  const [avaliador, setAvaliador] = useState("");
  const [respostas, setRespostas] = useState<RespostasFormulario>({ ...RESPOSTAS_VAZIAS, notas: {} });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  /** O que a pessoa já treinou — quem avalia precisa ver isso. */
  const [treinamentos, setTreinamentos] = useState<Row[]>([]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const colaborador = colaboradores.find((c) => String(c.id) === colaboradorId) ?? null;

  useEffect(() => {
    if (!colaboradorId) return setTreinamentos([]);
    let vivo = true;
    api
      .treinamentosDoColaborador(Number(colaboradorId))
      .then((t) => vivo && setTreinamentos(t))
      .catch(() => vivo && setTreinamentos([]));
    return () => {
      vivo = false;
    };
  }, [colaboradorId]);

  /** Sugere o formulário do cargo, dentro da empresa da pessoa. */
  function sugerir(c: Row | null) {
    if (!c) return;
    setModeloKey(modeloDoColaborador(c.cargo, c.departamento, empresaDoColaborador(c))?.key ?? "");
  }

  useEffect(() => {
    if (colaborador && !modeloKey) sugerir(colaborador);
    // Só na troca de colaborador: mexer no formulário escolhido à mão seria pior.
  }, [colaborador?.id]);

  /**
   * Trocar de empresa zera o que era da outra: manter o colaborador da SERCAMP
   * selecionado com os formulários da Move Me à mostra é justamente a mistura
   * que este campo existe para impedir.
   */
  function trocarEmpresa(nova: EmpresaKey) {
    setEmpresa(nova);
    setErro(null);
    const atual = colaboradores.find((c) => String(c.id) === colaboradorId) ?? null;
    if (atual && empresaDoColaborador(atual) === nova) {
      sugerir(atual);
      return;
    }
    setColaboradorId("");
    setModeloKey("");
  }

  const modelo = catalogo.modelos.find((m) => m.key === modeloKey) ?? null;
  const media = mediaDasNotas(respostas.notas);

  async function salvar() {
    setErro(null);
    if (!colaboradorId) return setErro("Escolha o colaborador.");
    if (!modelo) return setErro("Escolha o formulário da função.");
    if (media == null && !respostas.pontos_fortes && !respostas.pontos_melhoria) {
      return setErro("Preencha ao menos uma nota.");
    }
    setSalvando(true);
    try {
      await api.avaliacaoFormulario.aplicar({
        colaborador_id: Number(colaboradorId),
        periodo,
        avaliador: avaliador.trim() || undefined,
        modelo: modelo.key,
        respostas: respostas.notas,
        pontos_fortes: respostas.pontos_fortes,
        pontos_melhoria: respostas.pontos_melhoria,
        comentario: respostas.observacoes,
      });
      await onSalvo(
        `Avaliação de ${colaborador?.nome ?? "colaborador"} registrada${media != null ? ` — nota ${media.toFixed(1)}` : ""}.`,
      );
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Erro ao salvar a avaliação.");
      setSalvando(false);
    }
  }

  /**
   * Link para OUTRA pessoa responder a avaliação deste colaborador (o gestor
   * dele, por exemplo). Abre a mesma página pública do link de setor, mas com
   * uma pessoa só — e a resposta volta como avaliação dela.
   */
  async function gerarLink() {
    setErro(null);
    if (!colaborador) return setErro("Escolha o colaborador antes de gerar o link.");
    const responsavel = avaliador.trim();
    if (!responsavel) return setErro("Preencha o campo Avaliador: é o nome de quem vai responder.");
    setGerandoLink(true);
    try {
      const r = await api.avaliacaoLinks.criar({
        colaborador_id: Number(colaborador.id),
        gestor_nome: responsavel,
        periodo,
      });
      setLink({ url: `${window.location.origin}/avaliacao/${r.token}`, gestor: responsavel });
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível gerar o link.");
    } finally {
      setGerandoLink(false);
    }
  }

  /** Folha para assinar, no formato do formulário de papel. */
  function imprimir() {
    if (!modelo || !catalogo) return;
    const linhas = modelo.questoes
      .map(
        (q, i) => `<tr>
          <td>${i + 1}</td>
          <td><strong>${esc(q.titulo)}</strong>${q.descricao ? `<br><span class="peq">${esc(q.descricao)}</span>` : ""}</td>
          <td class="nota">${esc(respostas.notas[q.chave] ?? "")}</td>
        </tr>`,
      )
      .join("");

    const empresaNome = nomeDaEmpresa(empresa);
    const html = `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">
      <title>Avaliação de desempenho ${esc(empresaNome)} — ${esc(String(colaborador?.nome ?? ""))}</title>
      <style>
        body{font-family:system-ui,Segoe UI,sans-serif;color:#13241b;margin:26px}
        h1{font-size:18px;margin:0 0 2px} .cod{font-size:11px;color:#5e7268}
        .cab{border-bottom:2px solid #0c7a3f;padding-bottom:10px;margin-bottom:12px}
        .linha{font-size:12.5px;margin:2px 0}
        table{width:100%;border-collapse:collapse;font-size:12px;margin-top:8px}
        th,td{border:1px solid #cbd8d0;padding:5px 7px;text-align:left;vertical-align:top}
        th{background:#eaf5ee;font-size:11px;text-transform:uppercase}
        td.nota{width:70px;text-align:center;font-weight:700}
        .peq{font-size:11px;color:#5e7268}
        .escala{display:flex;gap:8px;margin:10px 0;font-size:11px}
        .escala div{flex:1;border:1px solid #cbd8d0;border-radius:6px;padding:5px 7px}
        .campo{margin-top:12px;font-size:12.5px}
        .campo b{display:block;margin-bottom:3px}
        .campo p{border:1px solid #cbd8d0;border-radius:6px;min-height:42px;padding:6px 8px;margin:0;white-space:pre-wrap}
        .ass{display:flex;gap:36px;margin-top:44px}
        .ass div{flex:1;border-top:1px solid #333;padding-top:5px;font-size:11.5px;text-align:center}
        @media print{body{margin:10mm}}
      </style></head><body>
      <div class="cab">
        <h1>Avaliação de Desempenho — ${esc(empresaNome)}</h1>
        <span class="cod">CÓDIGO: ${esc(catalogo.codigo_formulario)} · ${esc(modelo.nome)}</span>
        <p class="linha"><strong>Avaliado:</strong> ${esc(String(colaborador?.nome ?? "—"))} · ${esc(String(colaborador?.cargo ?? "—"))}</p>
        <p class="linha"><strong>Departamento:</strong> ${esc(String(colaborador?.departamento ?? modelo.departamento))} · <strong>Avaliador:</strong> ${esc(avaliador || "—")} · <strong>Período:</strong> ${esc(periodo || "—")}</p>
      </div>
      <div class="escala">
        ${catalogo.niveis.map((n) => `<div><strong>${esc(n.nivel)}</strong> (${esc(n.faixa)})<br>${esc(n.descricao)}</div>`).join("")}
      </div>
      <table><thead><tr><th>#</th><th>Questão</th><th>Nota</th></tr></thead><tbody>${linhas}</tbody></table>
      <p class="linha" style="margin-top:8px"><strong>TOTAL DE PONTOS (média):</strong> ${media != null ? media.toFixed(1) : "—"} — ${esc(nivelDe(media, catalogo.niveis))}</p>
      <div class="campo"><b>PONTOS FORTES:</b><p>${esc(respostas.pontos_fortes)}</p></div>
      <div class="campo"><b>PONTOS DE MELHORIAS:</b><p>${esc(respostas.pontos_melhoria)}</p></div>
      <div class="campo"><b>Observações:</b><p>${esc(respostas.observacoes)}</p></div>
      <div class="ass"><div>Avaliado</div><div>Avaliador</div><div>RH</div></div>
      </body></html>`;

    const janela = window.open("", "_blank");
    if (!janela) return setErro("O navegador bloqueou a janela de impressão. Libere os pop-ups.");
    janela.document.write(html);
    janela.document.close();
    janela.focus();
    janela.print();
  }

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card fo-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h3>Avaliação de desempenho — {nomeDaEmpresa(empresa)}</h3>
            <p className="modal-sub">
              Formulário {catalogo?.codigo_formulario ?? "FO 094"} da {nomeDaEmpresa(empresa)} — as
              perguntas são as da função do colaborador.
            </p>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          <>
              <div className="form-grid">
                {/* A empresa vem primeiro: é ela que define a lista de
                    colaboradores e o catálogo de formulários abaixo. */}
                <label className="field">
                  <span>
                    Modelo da avaliação <em className="req">*</em>
                  </span>
                  <select
                    value={empresa}
                    onChange={(e) => trocarEmpresa(e.target.value as EmpresaKey)}
                  >
                    {EMPRESAS.map((e) => (
                      <option key={e.key} value={e.key}>
                        Avaliação de desempenho {e.nome}
                      </option>
                    ))}
                  </select>
                  <small className="muted">
                    {listaColaboradores.length} colaborador(es) e {catalogo.modelos.length}{" "}
                    formulário(s) da {nomeDaEmpresa(empresa)}.
                  </small>
                </label>

                <label className="field">
                  <span>
                    Colaborador <em className="req">*</em>
                  </span>
                  <select
                    value={colaboradorId}
                    onChange={(e) => {
                      setColaboradorId(e.target.value);
                      setModeloKey("");
                      const c = colaboradores.find((x) => String(x.id) === e.target.value) ?? null;
                      sugerir(c);
                    }}
                  >
                    <option value="">Selecione…</option>
                    {listaColaboradores.map((c) => (
                      <option key={String(c.id)} value={String(c.id)}>
                        {c.nome}
                        {c.cargo ? ` — ${c.cargo}` : ""}
                      </option>
                    ))}
                  </select>
                  {listaColaboradores.length === 0 && (
                    <small className="muted">
                      Nenhum colaborador ativo da {nomeDaEmpresa(empresa)}. Marque a empresa na
                      ficha do colaborador (campo "Empresa").
                    </small>
                  )}
                </label>

                <label className="field">
                  <span>
                    Formulário da função <em className="req">*</em>
                  </span>
                  <select value={modeloKey} onChange={(e) => setModeloKey(e.target.value)}>
                    <option value="">Selecione…</option>
                    {/* Formulários por FUNÇÃO (cargo): A→Z só na exibição. */}
                    {ordenarPorNome(catalogo.modelos, (m) => m.nome).map((m) => (
                      <option key={m.key} value={m.key}>
                        {m.nome} — {m.departamento} ({m.questoes.length} questões)
                      </option>
                    ))}
                  </select>
                </label>

                <label className="field">
                  <span>Período</span>
                  <input value={periodo} onChange={(e) => setPeriodo(e.target.value)} placeholder="Ex.: 2026" />
                </label>

                <label className="field">
                  <span>Avaliador</span>
                  <input
                    value={avaliador}
                    onChange={(e) => setAvaliador(e.target.value)}
                    placeholder="Quem está avaliando"
                  />
                </label>
              </div>

              {treinamentos.length > 0 && (
                <details className="plano-avaliacoes">
                  <summary>
                    <IconCheck size={14} /> Plano de ação / treinamentos de{" "}
                    {String(colaborador?.nome ?? "").split(" ")[0]} ({treinamentos.length})
                  </summary>
                  <ul>
                    {treinamentos.map((t) => (
                      <li key={String(t.id)}>
                        <strong>{t.status === "Concluído" ? "✓" : "•"}</strong>
                        <span>
                          {String(t.nome_treinamento ?? "—")}
                          {t.carga_horaria ? ` · ${t.carga_horaria}h` : ""}
                          {t.data ? ` · ${String(t.data).slice(0, 10).split("-").reverse().join("/")}` : ""}
                        </span>
                        <small className="muted">
                          {String(t.status ?? "—")}
                          {t.nota != null ? ` · nota ${t.nota}` : ""}
                        </small>
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              <div className="fo-link">
                <div className="fo-link-topo">
                  <span>
                    <strong>Quer que outra pessoa responda?</strong> Gere o link desta avaliação e
                    mande para o responsável (o gestor, por exemplo). A resposta volta como avaliação
                    de {colaborador?.nome ?? "quem for escolhido"}.
                  </span>
                  <button
                    className="btn-secondary btn-sm"
                    onClick={gerarLink}
                    disabled={gerandoLink || !colaboradorId}
                  >
                    <IconLink size={15} /> {gerandoLink ? "Gerando…" : "Gerar link"}
                  </button>
                </div>
                {link && (
                  <div className="fo-link-pronto">
                    <input readOnly value={link.url} onFocus={(e) => e.currentTarget.select()} />
                    <button
                      className="btn-secondary btn-sm"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(link.url);
                          setCopiado(true);
                          setTimeout(() => setCopiado(false), 2000);
                        } catch {
                          setErro("Não foi possível copiar — selecione o link e copie à mão.");
                        }
                      }}
                    >
                      {copiado ? <IconCheck size={15} /> : <IconCopy size={15} />}{" "}
                      {copiado ? "Copiado!" : "Copiar"}
                    </button>
                    <a
                      className="btn-whats btn-sm"
                      href={`https://wa.me/?text=${encodeURIComponent(
                        `Olá, ${link.gestor}! Avaliação de desempenho de ${colaborador?.nome ?? ""}: ${link.url}`,
                      )}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      WhatsApp
                    </a>
                  </div>
                )}
              </div>

              <EscalaNiveis niveis={catalogo.niveis} />

              <FormularioAvaliacao
                modelo={modelo}
                niveis={catalogo.niveis}
                respostas={respostas}
                onChange={setRespostas}
              />
          </>
        </div>

        <div className="modal-foot fo-foot">
          {erro && <div className="form-error">{erro}</div>}
          <span className="fo-total-rodape">
            <IconStar size={15} /> {media != null ? media.toFixed(1) : "—"}
            <small>{nivelDe(media, catalogo.niveis)}</small>
          </span>
          <button type="button" className="btn-secondary" onClick={imprimir} disabled={!modelo}>
            <IconDownload size={16} /> Imprimir
          </button>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" onClick={salvar} disabled={salvando}>
            {salvando ? "Salvando…" : "Salvar avaliação"}
          </button>
        </div>
      </div>
    </div>
  );
}

function esc(s: string): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
